import { INTEGRATION_PARTNERS } from 'config/types';
import { motion } from 'framer-motion';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectPartner } from 'redux-stores/slices/authSlice';
import styled, { useTheme } from 'styled-components';

import { StyledArrowUpIcon, StyledContentContainer, StyledSpan } from './Accordion.styles';

export interface AccordionProps {
  title: string;
  isDefaultExpanded?: boolean;
  dropDownIcon?: {
    openIcon: React.ReactNode;
    closeIcon: React.ReactNode;
  };
  headerStyle?: React.CSSProperties;
  headerTitleStyle?: React.CSSProperties;
  contentContainerStyle?: React.CSSProperties;
  contentContainerContentStyle?: React.CSSProperties;
  dropDownText?: string;
  children?: React.ReactNode;
  isInverted?: boolean;
  hasNoPaddingX?: boolean;
}

const StyledHeading = styled.h3`
  color: ${({ theme }) => theme.components.accordion.title.color};
`;

// here we can define header config for radius for each state
const headerBorderRadiusConfig = {
  closed: '8px 8px 8px 8px',
  openInverted: '0px 0px 8px 8px',
  openInvertedCareem: '0px 0px 0px 0px',
  closedInverted: '8px 8px 0px 0px',
  openNotInverted: '8px 8px 0px 0px',
  default: '0px 0px 8px 8px',
};

// here we can define content config for radius for each state
const contentBorderRadiusConfig = {
  openInverted: '8px 8px 0px 0px',
  default: '0px 0px 8px 8px',
};

const Accordion: React.FC<AccordionProps> = ({
  title,
  children,
  isDefaultExpanded = false,
  headerStyle,
  contentContainerStyle,
  dropDownText,
  headerTitleStyle,
  dropDownIcon,
  isInverted,
  hasNoPaddingX,
  contentContainerContentStyle,
}) => {
  const [isOpen, setIsOpen] = React.useState(isDefaultExpanded);
  const partnerId = useSelector(selectPartner);
  const theme = useTheme();
  const toggleAccordion = () => setIsOpen((prev) => !prev);
  const headerBorderRadius = useMemo(() => {
    if (!isOpen && isInverted) {
      return headerBorderRadiusConfig.closedInverted;
    }
    if (!isOpen) {
      return headerBorderRadiusConfig.closed;
    }
    if (isOpen && isInverted && partnerId === INTEGRATION_PARTNERS.CAREEM) {
      return headerBorderRadiusConfig.openInvertedCareem;
    }
    if (isOpen && isInverted) {
      return headerBorderRadiusConfig.openInverted;
    }
    if (isOpen && !isInverted) {
      return headerBorderRadiusConfig.openNotInverted;
    }
    return headerBorderRadiusConfig.default;
  }, [isOpen, isInverted]);

  const contentBorderRadius = useMemo(() => {
    if (isOpen && isInverted) {
      return contentBorderRadiusConfig.openInverted;
    }
    return contentBorderRadiusConfig.default;
  }, [isOpen, isInverted]);

  const arrowOpenRotation = useMemo(() => (isInverted ? 180 : 0), [isInverted]);
  const arrowClosedRotation = useMemo(() => (isInverted ? 0 : 180), [isInverted]);

  const AccordionAnimated = useMemo(
    () =>
      [
        <motion.header
          initial={{
            border: theme.components.estimatedBillBottomSheet?.content.container.border,
            borderBottom: !isOpen || isInverted ? theme.components.estimatedBillBottomSheet?.content.container.border : 'unset',
            borderRadius: headerBorderRadius,
          }}
          className={`d-flex py-3 align-items-center ${hasNoPaddingX ? '' : 'px-5'}`}
          onClick={toggleAccordion}
          style={headerStyle}
          animate={{
            border: theme.components.estimatedBillBottomSheet?.content.container.border,
            borderBottom: !isOpen || isInverted ? theme.components.estimatedBillBottomSheet?.content.container.border : 'unset',
            borderRadius: headerBorderRadius,
          }}
          transition={{ delay: isOpen ? 0 : 0.4, ease: 'easeIn' }}
        >
          <StyledHeading style={headerTitleStyle} className="mb-0">
            {title}
          </StyledHeading>
          <StyledSpan className="margin-start-auto d-flex align-items-center">
            {dropDownText && (
              <StyledHeading style={headerTitleStyle} className="mb-0">
                {' '}
                {dropDownText}
              </StyledHeading>
            )}
            {dropDownIcon?.closeIcon ? (
              <motion.div
                initial={{
                  rotate: isOpen ? arrowOpenRotation : arrowClosedRotation,
                }}
                animate={{
                  rotate: isOpen ? arrowOpenRotation : arrowClosedRotation,
                }}
              >
                {dropDownIcon.closeIcon}
              </motion.div>
            ) : (
              <motion.div
                initial={{
                  rotate: isOpen ? arrowOpenRotation : arrowClosedRotation,
                }}
                animate={{
                  rotate: isOpen ? arrowOpenRotation : arrowClosedRotation,
                }}
              >
                <StyledArrowUpIcon />
              </motion.div>
            )}
          </StyledSpan>
        </motion.header>,
        <motion.section
          initial={{
            height: isOpen ? 'auto' : 0,
            paddingBottom: isOpen && !isInverted ? '16px' : '0px',
            borderBottom: isOpen ? theme.components.estimatedBillBottomSheet?.content.container.border : '0px',
            borderRadius: contentBorderRadius,
            borderTop: isInverted ? theme.components.estimatedBillBottomSheet?.content.container.border : '0px',
          }}
          animate={{
            height: isOpen ? 'auto' : 0,
            paddingBottom: isOpen && !isInverted ? '16px' : '0px',
            borderBottom: isOpen ? theme.components.estimatedBillBottomSheet?.content.container.border : '0px',
            borderRadius: contentBorderRadius,
            borderTop: isInverted && isOpen ? theme.components.estimatedBillBottomSheet?.content.container.border : '0px',
          }}
          className="overflow-hidden"
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          style={contentContainerStyle}
        >
          <StyledContentContainer $isinverted={`${Boolean(isInverted)}`} style={contentContainerContentStyle}>
            {children}
          </StyledContentContainer>
        </motion.section>,
      ].sort(() => (isInverted ? -1 : 1)),
    [
      isOpen,
      headerBorderRadius,
      isInverted,
      children,
      headerStyle,
      contentContainerStyle,
      dropDownText,
      headerTitleStyle,
      dropDownIcon,
      theme,
      contentContainerContentStyle,
      hasNoPaddingX,
    ]
  );

  // By using `AnimatePresence` to mount and unmount the contents, we can animate
  // them in and out while also only rendering the contents of open accordions
  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{AccordionAnimated}</>
  );
};

export default Accordion;
